import { BuildingOfficeIcon, TagIcon } from "@heroicons/react/24/outline";
import { Container, rem, Stack, Tabs, Text, Title } from "@mantine/core";
import { useMemo } from "react";
import { AliasManagement } from "../../components/admin/AliasManagement";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { DictionaryManagement } from "../../components/feature-specific/DictionaryManagement";
import {
  ConsolidateOfficeLocationsRequest,
  OfficeLocationCreateRequest,
  OfficeLocationUpdateRequest,
} from "../../models/officeLocation.model";
import { OfficeLocationAdminService } from "../../services/admin";

// Adapter type for office locations to work with DictionaryItem interface
interface OfficeLocationDictionaryAdapter {
  id: number;
  title: string;
  name: string;
  [key: string]: any;
}

export function OperationsOfficeLocations() {
  const officeLocationService = useMemo(
    () => new OfficeLocationAdminService(),
    []
  );

  // Adapt OfficeLocationAdminService to DictionaryService interface
  const officeLocationDictionaryService = useMemo(
    () => ({
      getDictionary: async () => {
        const locations =
          await officeLocationService.getOfficeLocationsDictionary();
        return locations.map((location) => ({
          ...location,
          title: location.name, // Map name to title for consistency with the DictionaryItem interface
          name: location.name,
        }));
      },
      createItem: async (item: Omit<OfficeLocationDictionaryAdapter, "id">) => {
        const result = await officeLocationService.createOfficeLocation({
          name: item.name,
        } as OfficeLocationCreateRequest);
        return {
          ...result,
          title: result.name, // Map name to title for consistency
          name: result.name,
        };
      },
      updateItem: async (
        id: number,
        item: Partial<OfficeLocationDictionaryAdapter>
      ) => {
        const result = await officeLocationService.updateOfficeLocation(id, {
          id,
          name: item.name!,
        } as OfficeLocationUpdateRequest);
        return {
          ...result,
          title: result.name, // Map name to title for consistency
          name: result.name,
        };
      },
      deleteItem: (id: number) =>
        officeLocationService.deleteOfficeLocation(id),
      consolidateItems: async (sourceId: number, targetId: number) => {
        const result = await officeLocationService.consolidateOfficeLocations({
          sourceOfficeLocationId: sourceId,
          targetOfficeLocationId: targetId,
        } as ConsolidateOfficeLocationsRequest);
        return {
          ...result,
          title: result.name, // Map name to title for consistency
          name: result.name,
        };
      },
    }),
    [officeLocationService]
  );

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Breadcrumb
            items={[
              { label: "Operations Panel", href: "/operations" },
              { label: "Dictionaries", href: "/operations/dictionaries" },
              { label: "Office Locations", isActive: true },
            ]}
          />
          <Title order={1} size="h2" fw={700}>
            Office Locations Management
          </Title>
          <Text c="dimmed" size="lg">
            Manage office locations and geographical information
          </Text>
        </div>

        {/* Tabbed Interface for Different Management Functions */}
        <Tabs defaultValue="dictionary" variant="default">
          <Tabs.List mb="md">
            <Tabs.Tab
              value="dictionary"
              leftSection={<BuildingOfficeIcon style={{ width: rem(16) }} />}
            >
              Office Locations
            </Tabs.Tab>
            <Tabs.Tab
              value="aliases"
              leftSection={<TagIcon style={{ width: rem(16) }} />}
            >
              Alias Management
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="dictionary">
            <DictionaryManagement
              title="Office Locations"
              service={officeLocationDictionaryService}
              itemDisplayName="Office Location"
              itemDisplayNamePlural="Office Locations"
              canConsolidate={true}
              titleFieldName="name"
              searchPlaceholder="Search office locations..."
              showReviewStatus={true}
              showNormalizedText={true}
              normalizedFieldName="normalizedName"
              onApproveItem={async (item) => {
                await officeLocationService.markAsReviewed(item.id);
              }}
              onRejectItem={async (item) => {
                await officeLocationService.deleteOfficeLocation(item.id);
              }}
            />
          </Tabs.Panel>

          <Tabs.Panel value="aliases">
            <AliasManagement officeLocationService={officeLocationService} />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
